import { bookingWallClockToUtc } from './bookingInstant';
import { parseWallClockInstant } from './wallClockInstant';
import type { CancellationPolicy } from '../models/CancellationPolicy';
import { PaymentStatus } from '../models/RoomBooking';

/** Minimal i18n `t` shape (avoids coupling tests to TFunction). */
export type CancellationPolicyTranslateFn = (
  key: string,
  defaultValue: string,
  options?: Record<string, unknown>,
) => string;

export type CancellationPolicyTone = 'free' | 'partial' | 'none' | 'unavailable';

export type CancellationPolicyDisplay = {
  /** Accessible full sentence. */
  fullText: string;
  /** Text before the deadline, e.g. "Free cancellation until". */
  lead?: string;
  /** Absolute deadline label, e.g. "Jul 23, 2026, 14:00". */
  deadline?: string;
  /** Text after the deadline, e.g. ", then 50% refund" or " for the first session". */
  trail?: string;
  /**
   * Series checkout, once the dated deadline has passed.
   * The status above is for the first session only.
   */
  scopeNote?: string;
  tone: CancellationPolicyTone;
};

const DEADLINE_FORMAT: Intl.DateTimeFormatOptions = {
  year: 'numeric',
  month: 'short',
  day: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
};

function normalizeTimezone(timezone: string | null | undefined): string | null {
  if (typeof timezone !== 'string') return null;
  const trimmed = timezone.trim();
  return trimmed.length > 0 ? trimmed : null;
}

/**
 * UTC instant for booking start: `startTime` is wall clock in `timezone`
 * (falls back to browser-local parse when timezone is missing).
 */
export function getBookingStartUtc(
  startTime: string,
  timezone?: string | null,
): Date {
  const zone = normalizeTimezone(timezone);
  if (!zone) {
    return parseWallClockInstant(startTime);
  }
  return bookingWallClockToUtc(parseWallClockInstant(startTime), zone);
}

/** Deadline UTC instant: booking start minus policy hours. */
export function getCancellationDeadlineUtc(
  startTime: string,
  deadlineHours: number,
  timezone?: string | null,
): Date {
  const startUtc = getBookingStartUtc(startTime, timezone);
  return new Date(startUtc.getTime() - deadlineHours * 60 * 60 * 1000);
}

/** @deprecated Prefer {@link getCancellationDeadlineUtc}. */
export function getCancellationDeadlineDate(
  startTime: string,
  deadlineHours: number,
  timezone?: string | null,
): Date {
  return getCancellationDeadlineUtc(startTime, deadlineHours, timezone);
}

export function formatCancellationDeadlineLabel(
  startTime: string,
  deadlineHours: number,
  locale: string,
  timezone?: string | null,
): string {
  const deadlineUtc = getCancellationDeadlineUtc(startTime, deadlineHours, timezone);
  const zone = normalizeTimezone(timezone);
  return deadlineUtc.toLocaleString(locale, {
    ...DEADLINE_FORMAT,
    ...(zone ? { timeZone: zone } : {}),
  });
}

export function isCancellationDeadlinePassed(
  startTime: string,
  deadlineHours: number,
  timezone?: string | null,
  now: Date = new Date(),
): boolean {
  return (
    getCancellationDeadlineUtc(startTime, deadlineHours, timezone).getTime() <=
    now.getTime()
  );
}

/**
 * Resolves org cancellation policy into display parts.
 * Deadline is computed in the booking `timezone` (space / tutor display zone).
 */
export function resolveCancellationPolicyDisplay(
  startTime: string,
  policy: CancellationPolicy | null | undefined,
  t: CancellationPolicyTranslateFn,
  locale: string,
  timezone?: string | null,
  now: Date = new Date(),
  /** More than one means the dated deadline is the first session. */
  sessionCount = 1,
): CancellationPolicyDisplay {
  if (!policy) {
    return {
      fullText: t(
        'cancellationPolicy.unavailable',
        'Cancellation policy unavailable',
      ),
      tone: 'unavailable',
    };
  }

  const deadlineHours = policy.cancellationDeadlineHours ?? 0;
  const before = policy.refundPercentageBeforeDeadline ?? 0;
  const after = policy.refundPercentageAfterDeadline ?? 0;

  if (before === 100 && after === 0 && deadlineHours === 0) {
    const fullText = t('cancellationPolicy.freeAnytime', 'Free cancellation anytime');
    return { fullText, tone: 'free' };
  }

  if (before === 0 && after === 0) {
    const fullText = t('cancellationPolicy.nonRefundable', 'Non-refundable');
    return { fullText, tone: 'none' };
  }

  // The dated deadline is the first session. Later sessions are not spelled out.
  const series = sessionCount > 1;
  const firstMark = series
    ? ` ${t('cancellationPolicy.firstSessionMark', 'for the first session')}`
    : '';
  const dated = (deadlineLabel: string) => `${deadlineLabel}${firstMark}`;
  const trailWithMark = (trail: string | undefined): string | undefined => {
    if (!series) return trail;
    if (!trail) return firstMark;
    return `${firstMark}${trail}`;
  };

  const deadlinePassed =
    deadlineHours > 0 &&
    isCancellationDeadlinePassed(startTime, deadlineHours, timezone, now);

  if (deadlinePassed) {
    const scopeNote = series
      ? t(
          'cancellationPolicy.seriesFirstSessionApplies',
          'Applies to the first session.',
        )
      : undefined;
    if (after <= 0) {
      const fullText = t('cancellationPolicy.nonRefundable', 'Non-refundable');
      return { fullText, scopeNote, tone: 'none' };
    }
    const fullText = t(
      'cancellationPolicy.partialRefundNow',
      '{{after}}% refund if cancelled now',
      { after },
    );
    return { fullText, scopeNote, tone: 'partial' };
  }

  const deadline = formatCancellationDeadlineLabel(
    startTime,
    deadlineHours,
    locale,
    timezone,
  );
  const shownDeadline = dated(deadline);

  if (before === 100 && after === 0) {
    const lead = t('cancellationPolicy.freeUntilLead', 'Free cancellation until');
    const fullText = t(
      'cancellationPolicy.freeUntil',
      'Free cancellation until {{deadline}}',
      { deadline: shownDeadline },
    );
    return { fullText, lead, deadline, trail: trailWithMark(undefined), tone: 'free' };
  }

  if (before === 100 && after > 0) {
    const lead = t('cancellationPolicy.freeUntilLead', 'Free cancellation until');
    const trail = t(
      'cancellationPolicy.thenPartialTrail',
      ', then {{after}}% refund',
      { after },
    );
    const fullText = t(
      'cancellationPolicy.freeUntilThenPartial',
      'Free cancellation until {{deadline}}, then {{after}}% refund',
      { deadline: shownDeadline, after },
    );
    return { fullText, lead, deadline, trail: trailWithMark(trail), tone: 'partial' };
  }

  const lead = t('cancellationPolicy.refundUntilLead', 'Refund {{before}}% until', {
    before,
  });
  const trail = t('cancellationPolicy.thenPercentTrail', ', then {{after}}%', {
    after,
  });
  const fullText = t(
    'cancellationPolicy.refundBeforeThenAfter',
    'Refund {{before}}% until {{deadline}}, then {{after}}%',
    { before, deadline: shownDeadline, after },
  );
  return { fullText, lead, deadline, trail: trailWithMark(trail), tone: 'partial' };
}

/**
 * Refund / deadline banner only when payment was captured.
 * Backend skips partial refunds unless `PAID`; auth holds and pay-on-site
 * have nothing to refund yet.
 */
export function shouldShowCancellationPolicyForPayment(
  paymentStatus: PaymentStatus | string | null | undefined,
): boolean {
  return paymentStatus === PaymentStatus.PAID || paymentStatus === 'PAID';
}

/** String form of {@link resolveCancellationPolicyDisplay}. */
export function formatCancellationPolicy(
  startTime: string,
  policy: CancellationPolicy | null | undefined,
  t: CancellationPolicyTranslateFn,
  locale = 'en',
  timezone?: string | null,
  now: Date = new Date(),
  sessionCount = 1,
): string {
  return resolveCancellationPolicyDisplay(
    startTime,
    policy,
    t,
    locale,
    timezone,
    now,
    sessionCount,
  ).fullText;
}
